import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useToast } from '../contexts/ToastContext';
import { usePageTitle } from '../hooks/usePageTitle';
import LoadingSpinner from '../components/LoadingSpinner';

const AcceptInvitePage: React.FC = () => {
  usePageTitle('Accept Invite');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, tokens, isInitializing } = useAuthStore();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  useEffect(() => {
    const acceptInvite = async () => {
      if (!token) {
        setError('Invalid invite link.');
        setLoading(false);
        return;
      }

      // Wait until auth has finished initializing before deciding what to show
      if (isInitializing) {
        return;
      }

      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/organizations/accept-invite/${token}/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens?.access}`,
          },
        });

        const data = await response.json();

        if (!response.ok) {
          setError(data.error || 'Failed to accept invite.');
          setLoading(false);
          return;
        }

        showSuccess('Invite Accepted!', data.message || 'You have successfully joined the organization.');
        setTimeout(() => {
          navigate('/app/organization', { replace: true });
        }, 1500);
      } catch {
        setError('Something went wrong. Please try again.');
        setLoading(false);
      }
    };

    acceptInvite();
  }, [token, isAuthenticated, isInitializing, tokens, navigate, showSuccess, showError, API_BASE_URL]);

  if (!isAuthenticated && !loading && !error) {
    const redirectUrl = `/accept-invite/${token}`;
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-violet-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">You've been invited!</h2>
            <p className="text-gray-600">
              Sign in or create an account to join the organization.
            </p>
          </div>

          <div className="space-y-3">
            <Link
              to={`/login?redirect=${encodeURIComponent(redirectUrl)}`}
              className="block w-full py-3 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-xl transition-colors"
            >
              Sign In
            </Link>
            <Link
              to={`/register?redirect=${encodeURIComponent(redirectUrl)}`}
              className="block w-full py-3 bg-white hover:bg-gray-50 text-violet-600 font-semibold rounded-xl border-2 border-violet-600 transition-colors"
            >
              Create Account
            </Link>
          </div>

          <p className="mt-6 text-xs text-gray-500">
            Already have an account?{' '}
            <Link to={`/login?redirect=${encodeURIComponent(redirectUrl)}`} className="text-violet-600 hover:text-violet-700 font-medium">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-gray-600">Accepting invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-6 max-w-md w-full text-center">
          <div className="mb-4">
            <svg className="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Could Not Accept Invite</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/app/dashboard')}
            className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white font-medium rounded-lg transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default AcceptInvitePage;
